import { Grid2, Stack } from "@mui/material";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { IconButton, Tooltip } from "@mui/material";

import { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { PayableMonth, Recurrence } from "../services/payable";
import { MRT_ColumnDef } from "material-react-table";
import { formatPrice } from "../helpers/format";
import VirtualizedTable from "../components/VirtualizeTable";
import { useUserStore } from "../stores/user";
import PayableDelete from "./PayableDelete";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import PayableEdit from "./PayableEdit";

const PayableRecurring = () => {
  const { user } = useUserStore();
  const [editOpen, setEditOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState(null);
  const [id, setId] = useState("");
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["payableRecurrence"],
    queryFn: async () => await Recurrence({ storeId: user?.storeId }),
    enabled: !!user?.storeId,
  });

  type PayableItem = Awaited<ReturnType<typeof PayableMonth>>["month"][0];

  const afterChange = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ["indicators", user?.storeId] });
  };

  const recurrenceMap: Record<string, string> = {
    ONETIME: "Única",
    MONTHLY: "Mensal",
    BIMONTHLY: "Bimestral",
    QUARTERLY: "Trimestral",
    SEMIANNUAL: "Semestral",
    ANNUAL: "Anual",
  };

  const columns = useMemo<MRT_ColumnDef<PayableItem>[]>(() => {
    return [
      {
        accessorKey: "title",
        header: "Conta",
        size: 170,
      },
      {
        accessorKey: "amount",
        header: "Valor",
        size: 60,
        Cell: ({ cell }) => formatPrice(cell.getValue() as number) || "--",
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        accessorKey: "nextPayble",
        header: "Proxima cobrança",
        size: 100,
        Cell: ({ cell }) => {
          const v = cell.getValue() as string | null;
          return v ? moment.utc(v).format("DD/MM/YYYY") : "--";
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: { textAlign: "center" },
        },
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            textAlign: "center",
            overflow: "visible",
          },
        },
      },
      {
        accessorKey: "recurrence",
        header: "Recorrência",
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return recurrenceMap[value] || "--";
        },
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        id: "editar",
        header: "Editar",
        size: 30,
        Cell: ({ row }) => {
          return (
            <Stack direction="row" spacing={1} justifyContent="center">
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedPayable(row.original);
                    setEditOpen(true);
                  }}
                >
                  <IconEdit size={16} />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        id: "delete",
        header: "Excluir",
        size: 50,
        Cell: ({ row }: any) => (
          <IconTrash
            onClick={() => {
              setId(row.original.id);
              setOpen(true);
            }}
            style={{ cursor: "pointer", color: "#d32f2f" }}
          />
        ),
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        enableSorting: false,
        enableColumnActions: false,
      },
    ];
  }, [data]);

  return (
    <>
      <Grid2 size={12}>
        <PayableDelete
          open={open}
          id={id}
          onClose={() => setOpen(false)}
          onAction={afterChange}
          isRecurring
        />
        {editOpen && (
          <PayableEdit
            open={editOpen}
            onClose={() => setEditOpen(false)}
            refetch={afterChange}
            payable={selectedPayable}
            recurrence
            isRecurring={false}
          />
        )}
        <VirtualizedTable
          data={data || []}
          columns={columns}
          fetchNextPage={() => Promise.resolve()}
          isLoading={isLoading || isFetching}
        />
      </Grid2>
    </>
  );
};

export default PayableRecurring;
